import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  Container,
  Button,
  FormActions,
  FormNotice,
  FormSection,
  FormSummary,
  FormSummaryHeading,
  FormSummaryMeta,
  StatusBadge,
} from 'components';
import { checkPermissions, globalPropTypes } from 'helpers';
import { formatDate, formatDateTime } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadMaMeetingApplicationCreator,
  saveMaMeetingApplicationCreator,
  removeMaMeetingApplicationCreator,
} from 'store/actionsCreators';
import { describeSubmitter } from './submitter';
import { SummaryList } from './Application.styled';
import Questionnaire from './Questionnaire';
import StatusHistory from './StatusHistory';

/**
 * The questionnaire a Member Association fills in to apply to a meeting.
 *
 * Every meeting type has its own fixed set of questions, and follow up
 * questions only appear once the answer they hang on is given. The same screen
 * renders the read only view of a sent application, with every input disabled.
 */
const Application = ({
  meetingType,
  mode,
  data,
  errors,
  userPermissions,
  loadApplication,
  saveApplication,
  clearApplication,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { meetingId, applicationId } = useParams();

  const [answers, setAnswers] = useState({});
  const [proxy, setProxy] = useState(null);

  const neededRole =
    mode === 'view' ? `ma_meeting_view_${meetingType}` : `ma_meeting_apply_${meetingType}`;
  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: [neededRole] }, userPermissions || []),
  );

  useEffect(() => {
    if (!allowed) return undefined;

    loadApplication({ meetingId, applicationId });

    return () => clearApplication();
  }, [meetingId, applicationId, allowed]);

  useEffect(() => {
    if (!data) return;

    setAnswers(data.answers || {});
    setProxy(data.proxyMemberAssociation || null);
  }, [data]);

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  if (!data) {
    return <PageContent />;
  }

  const { meeting, application, proxyOptions, votingRepresentative } = data;
  // Who sent it in: a delegate, a substitute and an observer may all fill the
  // form of their Member Association, so the screen names the one that did.
  const submittedBy = describeSubmitter(data.submittedBy);

  // An observer application is approved by the Chair of the CoD before IPMA
  // decides on it. The form says so up front, and once it is sent the notice
  // says what it is actually waiting for.
  let chairNotice = null;
  if (data.awaitsChair) {
    chairNotice = t('page.ma.meetings.application.awaitsChair');
  } else if (data.requiresChairApproval && !application) {
    chairNotice = t('page.ma.meetings.application.chairApprovalNeeded');
  }
  const status = application ? application.status : null;
  const readOnly = mode === 'view' || !data.editable;
  const disabled = readOnly;

  const setAnswer = (question, value) => {
    setAnswers((previous) => ({ ...previous, [question]: value }));
  };

  const submitForm = (submit) => {
    saveApplication(
      {
        applicationId: application ? application.id : null,
        meetingId: meeting.id,
        meetingType,
        answers,
        proxyMemberAssociation: proxy,
        submit,
      },
      history,
    );
  };

  const renderActions = () => {
    if (readOnly) {
      return (
        <FormActions>
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push(`/ma/meetings/${meetingType}`)}
          >
            {t('page.ma.meetings.application.back')}
          </Button>
        </FormActions>
      );
    }

    return (
      <FormActions>
        {status !== 'SUBMITTED' && (
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => submitForm(false)}
          >
            {t('page.ma.meetings.application.saveDraft')}
          </Button>
        )}
        <Button type="button" style={{ width: 'auto' }} onClick={() => submitForm(true)}>
          {status === 'SUBMITTED'
            ? t('page.ma.meetings.application.saveChanges')
            : t('page.ma.meetings.application.submit')}
        </Button>
        <Button
          type="button"
          secondary
          style={{ width: 'auto' }}
          onClick={() => history.push(`/ma/meetings/${meetingType}`)}
        >
          {t('global.cancelButton')}
        </Button>
      </FormActions>
    );
  };

  return (
    <PageContent>
      <Container>
        <form>
          <FormSummary>
            <div>
              <FormSummaryHeading>{meeting.name}</FormSummaryHeading>
              <FormSummaryMeta>
                {t('page.ma.meetings.application.meetingSummary', {
                  date: formatDate(meeting.date),
                  delivery: t(
                    meeting.onsite
                      ? 'page.ma.meetings.application.onsite'
                      : 'page.ma.meetings.application.online',
                  ),
                })}
              </FormSummaryMeta>
            </div>
            {application ? (
              <StatusBadge status={application.status}>{application.statusLabel}</StatusBadge>
            ) : null}
          </FormSummary>

          {data.rejectionReason ? (
            <FormNotice
              type={application && application.status === 'REJECTED' ? 'error' : 'success'}
            >
              {t('page.ma.meetings.application.reason', { reason: data.rejectionReason })}
            </FormNotice>
          ) : null}
          {submittedBy ? (
            <FormSection title={t('page.ipma.meetings.application.applicantSection')}>
              <SummaryList>
                <dt>{t('page.ma.meetings.application.submittedBy')}</dt>
                <dd>{submittedBy}</dd>
                <dt>{t('page.ipma.meetings.application.submittedAt')}</dt>
                <dd>{formatDateTime(application.submittedAt) || '-'}</dd>
              </SummaryList>
            </FormSection>
          ) : null}
          {chairNotice ? <FormNotice type="info">{chairNotice}</FormNotice> : null}
          {!data.editable && !readOnly ? (
            <FormNotice type="warning">{t('page.ma.meetings.application.closed')}</FormNotice>
          ) : null}

          <Questionnaire
            meeting={meeting}
            answers={answers}
            errors={errors}
            disabled={disabled}
            onAnswer={setAnswer}
            proxy={proxy}
            proxyOptions={proxyOptions}
            onProxy={setProxy}
            proxyApproved={data.proxyApproved}
            votingRepresentative={votingRepresentative}
            observer={!!data.observer}
          />

          {renderActions()}

          {application && <StatusHistory items={data.statusHistory} />}
        </form>
      </Container>
    </PageContent>
  );
};

Application.propTypes = {
  meetingType: PropTypes.oneOf(['cod', 'csp']).isRequired,
  mode: PropTypes.oneOf(['create', 'edit', 'view']).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  data: PropTypes.shape({
    meeting: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      type: PropTypes.string,
      onsite: PropTypes.bool,
      date: PropTypes.string,
    }),
    application: PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      submittedAt: PropTypes.string,
      verified: PropTypes.bool,
      flag: PropTypes.string,
    }),
    answers: PropTypes.shape({}),
    proxyMemberAssociation: PropTypes.number,
    proxyApproved: PropTypes.bool,
    proxyOptions: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }),
    ),
    votingRepresentative: PropTypes.string,
    editable: PropTypes.bool,
    rejectionReason: PropTypes.string,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
    observer: PropTypes.bool,
    requiresChairApproval: PropTypes.bool,
    awaitsChair: PropTypes.bool,
    submittedBy: PropTypes.shape({
      name: PropTypes.string,
      organization: PropTypes.string,
      role: PropTypes.string,
    }),
  }),
  errors: PropTypes.objectOf(PropTypes.string),
  loadApplication: PropTypes.func.isRequired,
  saveApplication: PropTypes.func.isRequired,
  clearApplication: PropTypes.func.isRequired,
};

Application.defaultProps = {
  data: null,
  errors: {},
};

const mapStateToProps = ({ ma, user }) => ({
  data: ma.meetings.application.data,
  errors: ma.meetings.application.errors,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadApplication: (payload) => dispatch(loadMaMeetingApplicationCreator(payload)),
  saveApplication: (payload, history) =>
    dispatch(saveMaMeetingApplicationCreator(payload, history)),
  clearApplication: () => dispatch(removeMaMeetingApplicationCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Application);
